import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface Item {
  to: string;
  label: string;
  icon: string;
  disabled?: boolean;
  hint?: string;
}

interface Group {
  label: string;
  icon: string;
  prefix: string; // url prefix : ouvre auto si on est sur une route correspondante
  children: Item[];
}

const items: Item[] = [
  { to: '/', label: 'Dashboard', icon: '📊' },
  { to: '/ventes', label: 'Ventes & Devis', icon: '📋' },
  { to: '/import-historique', label: 'Import historique', icon: '⬆' },
];

const groups: Group[] = [
  {
    label: 'CRM',
    icon: '👥',
    prefix: '/contacts',
    children: [
      { to: '/contacts', label: 'Contacts', icon: '·' },
      { to: '/contacts/pipeline', label: 'Pipeline', icon: '·' },
      { to: '/contacts/relances', label: 'Relances', icon: '·' },
    ],
  },
  {
    label: 'Stock',
    icon: '📦',
    prefix: '/references',
    children: [
      { to: '/references', label: 'Références', icon: '·' },
      { to: '/containers', label: 'Containers', icon: '·' },
    ],
  },
  {
    label: 'Trésorerie',
    icon: '💶',
    prefix: '/tresorerie',
    children: [
      { to: '/tresorerie', label: "Vue d'ensemble", icon: '·' },
      { to: '/tresorerie/cashflow', label: 'Cash flow', icon: '·' },
      { to: '/tresorerie/encaissements', label: 'Encaissements', icon: '·' },
      { to: '/tresorerie/acomptes', label: 'Acomptes', icon: '·' },
      { to: '/tresorerie/avoirs', label: 'Avoirs', icon: '·' },
      { to: '/tresorerie/charges-fixes', label: 'Charges fixes', icon: '·' },
      { to: '/tresorerie/global-wood', label: 'Global Wood', icon: '·' },
    ],
  },
];

const disabledItems: Item[] = [];

export function Sidebar(): JSX.Element {
  const location = useLocation();
  const [openCRM, setOpenCRM] = useState(location.pathname.startsWith('/contacts'));
  const [openStock, setOpenStock] = useState(
    location.pathname.startsWith('/references') || location.pathname.startsWith('/containers'),
  );
  const [openTreso, setOpenTreso] = useState(location.pathname.startsWith('/tresorerie'));

  return (
    <aside className="w-60 shrink-0 bg-slate-950 border-r border-slate-800 flex flex-col">
      <div className="px-5 py-4 border-b border-slate-800">
        <div className="text-floryon-green font-bold text-lg leading-tight">Cockpit</div>
        <div className="text-slate-500 text-xs">Floryon</div>
      </div>
      <nav className="flex-1 py-3">
        {items.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }): string =>
              `flex items-center gap-3 px-5 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-floryon-green/10 text-floryon-green border-l-2 border-floryon-green'
                  : 'text-slate-300 hover:bg-slate-800/50 border-l-2 border-transparent'
              }`
            }
          >
            <span className="text-base">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}

        {groups.map((g) => {
          // Détecte si on est sur une route de ce groupe (prefix OU autres prefixes Stock).
          const isOnGroup =
            g.label === 'Stock'
              ? location.pathname.startsWith('/references') ||
                location.pathname.startsWith('/containers')
              : location.pathname.startsWith(g.prefix);
          const open =
            g.label === 'Stock' ? openStock : g.label === 'Trésorerie' ? openTreso : openCRM;
          const toggle = (): void => {
            if (g.label === 'Stock') setOpenStock((v) => !v);
            else if (g.label === 'Trésorerie') setOpenTreso((v) => !v);
            else setOpenCRM((v) => !v);
          };
          return (
            <div key={g.label}>
              <button
                type="button"
                onClick={toggle}
                className={`w-full flex items-center justify-between px-5 py-2 text-sm transition-colors ${
                  isOnGroup
                    ? 'text-floryon-green border-l-2 border-floryon-green bg-floryon-green/10'
                    : 'text-slate-300 hover:bg-slate-800/50 border-l-2 border-transparent'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-base">{g.icon}</span>
                  <span>{g.label}</span>
                </span>
                <span className="text-xs text-slate-500">{open ? '▾' : '▸'}</span>
              </button>
              {open && (
                <div className="bg-slate-950">
                  {g.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      end
                      className={({ isActive }): string =>
                        `flex items-center gap-3 pl-12 pr-5 py-1.5 text-xs transition-colors ${
                          isActive
                            ? 'text-floryon-green bg-floryon-green/5'
                            : 'text-slate-400 hover:bg-slate-800/40'
                        }`
                      }
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {disabledItems.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between px-5 py-2 text-sm text-slate-600 cursor-not-allowed"
            title={item.hint ?? ''}
          >
            <span className="flex items-center gap-3">
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate-700">{item.hint}</span>
          </div>
        ))}
      </nav>
      <div className="px-5 py-3 text-[10px] text-slate-600 border-t border-slate-800">v0.1.0</div>
    </aside>
  );
}
